import { useCallback, useEffect, useRef, useState } from "react";
import { useChannelSplit } from "./ChannelSplitContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { Layers, Share2, Upload } from "lucide-react";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import {
    channelLabels,
    channelCount,
    type ChannelDistortionMethod,
} from "../../utils/channelSplitVector";
import {
    NOISE_DISPLACEMENT_STYLE_OPTIONS,
    type NoiseDisplacementStyle,
} from "../../utils/displacementMap";

const ROUGHEN_MAX = 80;
const OFFSET_MIN = -200;
const OFFSET_MAX = 200;
const OFFSET_DEBOUNCE_MS = 300;
const ROUGHEN_DEBOUNCE_MS = 200;
const ROTATION_MIN = -180;
const ROTATION_MAX = 180;

export function ChannelSplitControls() {
    const {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        compositeMode,
        setCompositeMode,
        distortionMethod,
        setDistortionMethod,
        pathNoiseStyle,
        setPathNoiseStyle,
        roughen,
        setRoughenAt,
        xOffsets,
        yOffsets,
        setXOffsetAt,
        setYOffsetAt,
        rotationsDeg,
        setRotationDegAt,
        resetOffsets,
        channelVisible,
        setChannelVisibleAt,
        resetChannelVisibility,
        exportSvgString,
        canExport,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useChannelSplit();

    const n = channelCount(compositeMode);
    const labels = channelLabels(compositeMode);

    const [roughenDraft, setRoughenDraft] = useState<number[]>(roughen);
    const roughenCommitTimeouts = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );

    useEffect(() => {
        roughenCommitTimeouts.current.forEach(clearTimeout);
        roughenCommitTimeouts.current.clear();
        setRoughenDraft(roughen);
    }, [roughen]);

    useEffect(
        () => () => {
            roughenCommitTimeouts.current.forEach(clearTimeout);
            roughenCommitTimeouts.current.clear();
        },
        [],
    );

    const handleRoughenInput = useCallback(
        (index: number, value: number) => {
            setRoughenDraft((prev) => {
                const next = [...prev];
                next[index] = value;
                return next;
            });
            const pending = roughenCommitTimeouts.current.get(index);
            if (pending) clearTimeout(pending);
            roughenCommitTimeouts.current.set(
                index,
                setTimeout(() => {
                    setRoughenAt(index, value);
                    roughenCommitTimeouts.current.delete(index);
                }, ROUGHEN_DEBOUNCE_MS),
            );
        },
        [setRoughenAt],
    );

    return (
        <div className="panel-controls channel-split-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Channel Split</h2>
                    <ToolDescriptionPopover dialogLabel="Channel Split tool description">
                        <p>
                            Duplicate imported vector art per channel with{" "}
                            <code>feColorMatrix</code>, stacked with screen
                            (RGB) or multiply (CMYK) on a dark or light
                            background.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="channel-split"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Source" icon={Upload}>
                    <label className="field-label">SVG file</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file…
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleSvgLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info">
                            Loaded: <strong>{svg.name}</strong>
                        </p>
                    )}
                    {error && <p className="upload-error">{error}</p>}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Compositing & warp" icon={Layers}>
                    <fieldset className="channel-split-mode-fieldset">
                        <legend className="field-label">Compositing</legend>
                        <label className="channel-split-radio">
                            <input
                                type="radio"
                                name="channel-split-mode"
                                checked={compositeMode === "rgb-screen"}
                                onChange={() => setCompositeMode("rgb-screen")}
                            />
                            RGB + screen
                        </label>
                        <label className="channel-split-radio">
                            <input
                                type="radio"
                                name="channel-split-mode"
                                checked={compositeMode === "cmyk-multiply"}
                                onChange={() =>
                                    setCompositeMode("cmyk-multiply")
                                }
                            />
                            CMYK + multiply
                        </label>
                    </fieldset>

                    <div className="field-row">
                        <label className="field-label">
                            Distortion method
                            <select
                                value={distortionMethod}
                                onChange={(e) =>
                                    setDistortionMethod(
                                        e.target
                                            .value as ChannelDistortionMethod,
                                    )
                                }
                            >
                                <option value="filter-displacement">
                                    Filter displacement
                                </option>
                                <option value="path-displacement">
                                    Path displacement (export-friendly)
                                </option>
                            </select>
                        </label>
                    </div>

                    {distortionMethod === "path-displacement" && (
                        <div className="field-row">
                            <label className="field-label">
                                Path warp style
                                <select
                                    value={pathNoiseStyle}
                                    onChange={(e) =>
                                        setPathNoiseStyle(
                                            e.target
                                                .value as NoiseDisplacementStyle,
                                        )
                                    }
                                >
                                    {NOISE_DISPLACEMENT_STYLE_OPTIONS.map(
                                        (o) => (
                                            <option
                                                key={o.value}
                                                value={o.value}
                                            >
                                                {o.label}
                                            </option>
                                        ),
                                    )}
                                </select>
                            </label>
                        </div>
                    )}

                    <div className="channel-split-roughen">
                        <span className="field-label">
                            Roughen per channel{" "}
                            {distortionMethod === "path-displacement"
                                ? "(path displacement strength)"
                                : "(filter displacement scale)"}
                        </span>
                        {Array.from({ length: n }, (_, i) => (
                            <label
                                key={labels[i]}
                                className="field-row channel-split-slider-row"
                            >
                                <span className="channel-split-slider-label">
                                    {labels[i]}
                                </span>
                                <input
                                    type="range"
                                    min={0}
                                    max={ROUGHEN_MAX}
                                    step={1}
                                    value={roughenDraft[i] ?? 0}
                                    onChange={(e) =>
                                        handleRoughenInput(
                                            i,
                                            Number(e.target.value),
                                        )
                                    }
                                />
                                <span className="channel-split-slider-value">
                                    {Math.round(roughenDraft[i] ?? 0)}
                                </span>
                            </label>
                        ))}
                    </div>

                    <div className="channel-split-roughen">
                        <div className="tool-header-actions">
                            <span className="field-label">
                                Per channel: visibility, X/Y offset, rotation (°
                                around viewBox center)
                            </span>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={resetChannelVisibility}
                            >
                                Show all
                            </button>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={resetOffsets}
                            >
                                Reset transforms
                            </button>
                        </div>
                        {Array.from({ length: n }, (_, i) => (
                            <div
                                key={`${labels[i]}-offset`}
                                className="channel-split-channel-row"
                            >
                                <label className="channel-split-channel-toggle">
                                    <input
                                        type="checkbox"
                                        checked={channelVisible[i] ?? true}
                                        onChange={(e) =>
                                            setChannelVisibleAt(i, e.target.checked)
                                        }
                                    />
                                    <span className="channel-split-slider-label">
                                        {labels[i]}
                                    </span>
                                </label>
                                <label className="channel-split-offset-input">
                                    X
                                    <DebouncedInput
                                        type="number"
                                        min={OFFSET_MIN}
                                        max={OFFSET_MAX}
                                        step={1}
                                        debounceMs={OFFSET_DEBOUNCE_MS}
                                        value={Math.round(xOffsets[i] ?? 0)}
                                        onChange={(v) =>
                                            setXOffsetAt(i, v as number)
                                        }
                                    />
                                </label>
                                <label className="channel-split-offset-input">
                                    Y
                                    <DebouncedInput
                                        type="number"
                                        min={OFFSET_MIN}
                                        max={OFFSET_MAX}
                                        step={1}
                                        debounceMs={OFFSET_DEBOUNCE_MS}
                                        value={Math.round(yOffsets[i] ?? 0)}
                                        onChange={(v) =>
                                            setYOffsetAt(i, v as number)
                                        }
                                    />
                                </label>
                                <label className="channel-split-offset-input">
                                    °
                                    <DebouncedInput
                                        type="number"
                                        min={ROTATION_MIN}
                                        max={ROTATION_MAX}
                                        step={1}
                                        debounceMs={OFFSET_DEBOUNCE_MS}
                                        value={Math.round(rotationsDeg[i] ?? 0)}
                                        onChange={(v) =>
                                            setRotationDegAt(i, v as number)
                                        }
                                    />
                                </label>
                            </div>
                        ))}
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport || !exportSvgString}
                        getSvg={() =>
                            exportSvgString
                                ? {
                                      name: "channel-split-export.svg",
                                      content: exportSvgString,
                                  }
                                : null
                        }
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["channel-split"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
